import { useEffect, useState, type FormEvent } from 'react';
import type { Project } from '@hangar/shared';
import { toast } from 'sonner';
import { useProjectMutations } from '@/api/hooks';
import { useCanConfigure } from '@/lib/permissions';
import { showErrorToast } from '@/lib/toasts';
import { limitsFailure } from './limitsFailure';
import { hasLimitError, initialLimitValues, limitErrorFor, limitPatch, limitsDirty, limitsSavedDescription, localLimitErrors, sanitizeLimit, type LimitKey, type LimitValues } from './limitsModel';

/** The three inputs, reloaded whenever the saved limits change. */
function useLimitValues(saved: Project['usageLimits']) {
  const [values, setValues] = useState<LimitValues>(() => initialLimitValues(saved));
  const savedKey = JSON.stringify(saved ?? null);
  useEffect(() => setValues(initialLimitValues(saved)), [savedKey]); // reload when the saved limits change
  return { values, setLimit: (key: LimitKey, raw: string) => setValues((v) => ({ ...v, [key]: sanitizeLimit(raw) })) };
}

/** State and submit of the Limits card (`PATCH /projects/{id}` `usageLimits`, merge patch). */
export function useLimitsForm(project: Project) {
  const { update } = useProjectMutations();
  const can = useCanConfigure(project.id);
  const { values, setLimit } = useLimitValues(project.usageLimits);
  const [server, setServer] = useState<Record<string, string>>({});
  const [problem, setProblem] = useState<unknown>(null);
  const [submitted, setSubmitted] = useState(false);
  const local = localLimitErrors(values);
  const patch = limitPatch(values, project.usageLimits);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    setServer({});
    setProblem(null);
    if (hasLimitError(local) || !limitsDirty(patch)) return;
    try {
      const p = await update.mutateAsync({ projectId: project.id, body: { usageLimits: patch }, etag: project.etag });
      toast.success('Limits saved', { description: limitsSavedDescription(p) });
      setSubmitted(false);
    } catch (err) {
      const failure = limitsFailure(err);
      if (failure.server) setServer(failure.server);
      if ('problem' in failure) setProblem(failure.problem);
      if (failure.toast) showErrorToast(failure.toast);
    }
  };
  const clearServer = (key: LimitKey) => setServer((s) => (s[key] ? { ...s, [key]: '' } : s));
  const errorFor = (key: LimitKey) => limitErrorFor(key, { server, local, submitted, values });
  return { values, setLimit, clearServer, can, dirty: limitsDirty(patch), problem, errorFor, onSubmit, isPending: update.isPending };
}

export type LimitsForm = ReturnType<typeof useLimitsForm>;
